import { ComponentPropsWithRef, HTMLAttributes } from "react"
import { useTranslation } from "react-i18next"
import { X } from "@phosphor-icons/react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { cva, VariantProps } from "class-variance-authority"
import { buttonVariants } from "@/components/ui/button"
import { cn } from "@/lib/utils"

const dialogContentVariants = cva(
  "dialog-content bg-background shadow-5 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] translate-x-custom translate-y-custom fixed top-[50%] left-[50%] z-50 grid gap-0 rounded-lg border duration-200",
  {
    variants: {
      variant: {
        small: "max-w-150 min-w-150",
        large: "max-w-300 min-w-300",
        fitContent: "w-max max-w-[90vw]",
      },
    },
    defaultVariants: { variant: "large" },
  },
)

export const Dialog = DialogPrimitive.Root

export const DialogTrigger = DialogPrimitive.Trigger

export const DialogPortal = DialogPrimitive.Portal

type DialogOverlayProps = ComponentPropsWithRef<typeof DialogPrimitive.Overlay>
export const DialogOverlay = ({ className, ...props }: DialogOverlayProps) => (
  <DialogPrimitive.Overlay
    className={cn(
      "bg-on-backdrop data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50",
      className,
    )}
    {...props}
  />
)

export interface DialogContentProps
  extends ComponentPropsWithRef<typeof DialogPrimitive.Content>, VariantProps<typeof dialogContentVariants> {
  "data-testid"?: string
}

export const DialogContentCustom = ({ className, children, variant, ...props }: DialogContentProps) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      aria-describedby=""
      className={cn(dialogContentVariants({ variant }), className)}
      {...props}>
      {children}
    </DialogPrimitive.Content>
  </DialogPortal>
)

export const DialogContent = ({ className, children, variant, ...props }: DialogContentProps) => (
  <DialogPrimitive.Content className={cn(dialogContentVariants({ variant }), className)} aria-describedby="" {...props}>
    {children}
  </DialogPrimitive.Content>
)

export const DialogClose = ({ className, ...props }: ComponentPropsWithRef<typeof DialogPrimitive.Close>) => {
  const { t } = useTranslation()
  return (
    <DialogPrimitive.Close
      className={cn(
        "ring-offset-background data-[state=open]:text-muted-foreground hover:bg-surface-hovered focus:ring-ring focus-visible:outline-primary-focused absolute top-5 right-4 z-50 cursor-pointer rounded-full p-2 opacity-70 transition-all hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden focus-visible:opacity-100 focus-visible:outline-offset-2 disabled:pointer-events-none",
        className,
      )}
      data-testid="dialog-close"
      autoFocus={false}
      tabIndex={-1}
      {...props}>
      <X />
      <span className="sr-only">{t("common.button.close")}</span>
    </DialogPrimitive.Close>
  )
}

type DialogHeaderProps = HTMLAttributes<HTMLDivElement> & Pick<DialogOverlayProps, "ref">
export const DialogHeader = ({ className, ...props }: DialogHeaderProps) => (
  <div className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)} {...props} />
)

export const DialogFooter = ({ className, ...props }: DialogHeaderProps) => (
  <div
    className={cn(
      "dialog-footer flex flex-col-reverse items-end px-6 pt-4 pb-6 sm:flex-row sm:justify-end sm:space-x-2",
      className,
    )}
    {...props}
  />
)

type DialogTitleProps = ComponentPropsWithRef<typeof DialogPrimitive.Title> & {
  titleTextClassName?: string
  closeButtonClassName?: string
}
export const DialogTitle = ({
  className,
  titleTextClassName,
  closeButtonClassName,
  children,
  ...props
}: DialogTitleProps) => (
  <DialogPrimitive.Title
    className={cn(
      "dialog-title-box flex items-start justify-between gap-4 self-stretch pt-4 pr-4 pb-2 pl-6",
      className,
    )}
    {...props}>
    <div
      className={cn(
        "dialog-title-box-content text-on-surface py-1.5 text-xl leading-7 font-bold tracking-normal wrap-break-word",
        titleTextClassName,
      )}
      data-testid="dialog-title">
      {children}
    </div>
    <DialogPrimitive.Close
      className={cn(
        buttonVariants({ variant: "circle" }),
        "dialog-title-close-btn box-content h-6 w-6 self-center p-2",
        closeButtonClassName,
      )}
      data-testid="dialog-close">
      <X />
      <span className="sr-only">Close</span>
    </DialogPrimitive.Close>
  </DialogPrimitive.Title>
)

type DialogDescriptionProps = ComponentPropsWithRef<typeof DialogPrimitive.Description>
export const DialogDescription = ({ className, ...props }: DialogDescriptionProps) => (
  <DialogPrimitive.Description
    className={cn("dialog-description text-on-surface-subdued px-6 py-2 text-sm", className)}
    {...props}
  />
)
